/**
 * MINI GERENCIAMENTO — MONITORES (Fase 1).
 *
 * O Monitor NÃO é um papel novo: é um ALUNO da equipe com um vínculo ativo. A
 * promoção não cria conta, não troca o papel (`users.role` continua `athlete`)
 * e não exige novo login — por isso esta tela só cuida do VÍNCULO.
 *
 * Quem pode o quê vem CALCULADO do servidor (`pode_promover` / `pode_remover`):
 * aqui só desenhamos o que o backend autorizou. Nesta fase o Monitor ainda não
 * acessa o módulo Minha Equipe.
 */
import { useCallback, useEffect, useState } from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { toast } from 'sonner';
import { Loader2, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import { minhaEquipeApi } from '../../lib/api';

const MOTIVOS_DE_RECUSA = {
  sem_conta: 'ainda não tem conta própria no e-Torneios',
  nao_e_meu_aluno: 'não é seu aluno',
  faixa_fora_da_regra: 'faixa fora de Branca–Marrom',
  ja_e_monitor: 'já é Monitor',
};

function dataCurta(iso) {
  if (!iso) return '';
  try {
    return new Date(iso).toLocaleDateString('pt-BR');
  } catch (e) {
    return '';
  }
}

export default function MonitoresEquipe() {
  const [dados, setDados] = useState({ monitores: [], candidatos: [], faixa_maxima: 'Marrom' });
  const [carregando, setCarregando] = useState(true);
  const [salvando, setSalvando] = useState(false);
  const [escolhido, setEscolhido] = useState('');
  const [motivoPromocao, setMotivoPromocao] = useState('');
  const [removendo, setRemovendo] = useState(null);
  const [motivoRemocao, setMotivoRemocao] = useState('');

  const carregar = useCallback(async () => {
    try {
      const { data } = await minhaEquipeApi.monitores();
      setDados(data || { monitores: [], candidatos: [] });
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao carregar os monitores');
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const candidatos = dados.candidatos || [];
  const elegiveis = candidatos.filter((c) => c.pode_promover);
  const monitores = dados.monitores || [];

  const promover = async () => {
    if (!escolhido) {
      toast.error('Escolha o aluno que vai virar Monitor');
      return;
    }
    setSalvando(true);
    try {
      await minhaEquipeApi.promoverMonitor(escolhido, motivoPromocao);
      toast.success('Aluno promovido a Monitor — ele continua sendo atleta');
      setEscolhido('');
      setMotivoPromocao('');
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível promover');
    } finally {
      setSalvando(false);
    }
  };

  const remover = async (userId) => {
    setSalvando(true);
    try {
      await minhaEquipeApi.removerMonitor(userId, motivoRemocao);
      toast.success('Vínculo encerrado — o usuário continua atleta');
      setRemovendo(null);
      setMotivoRemocao('');
      await carregar();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Não foi possível remover');
    } finally {
      setSalvando(false);
    }
  };

  if (carregando) {
    return (
      <div className="flex items-center gap-2 text-[#A1A1AA]" data-testid="monitores-carregando">
        <Loader2 className="animate-spin" size={16} /> Carregando monitores...
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="monitores-equipe">
      <div className="rounded-lg border border-[#27272A] bg-[#141414] p-4">
        <div className="flex items-center gap-2 text-white">
          <ShieldCheck size={18} className="text-[#D4AF37]" />
          <span className="font-medium">Monitor da equipe</span>
        </div>
        <p className="mt-1 text-sm text-[#A1A1AA]">
          O Monitor é um <strong>aluno da equipe</strong> com vínculo ativo: continua
          sendo atleta, mantém conta, carteirinha, inscrições e histórico. Só pode ser
          promovido o aluno da faixa <strong>Branca até a {dados.faixa_maxima}</strong>
          {' '}(incluindo as faixas infantis), pelo <strong>professor responsável</strong>.
          Chegar à faixa Preta não remove o vínculo.
        </p>
      </div>

      <div className="rounded-lg border border-[#27272A] bg-[#141414] p-4 space-y-3">
        <div className="flex items-center gap-2 text-white">
          <UserPlus size={16} className="text-[#D4AF37]" />
          <span className="font-medium">Promover um aluno</span>
        </div>
        {elegiveis.length === 0 ? (
          <p className="text-sm text-[#A1A1AA]" data-testid="monitores-sem-candidatos">
            Nenhum aluno elegível no momento. O aluno precisa ter conta própria, ser seu
            aluno (professor responsável) e estar entre a Branca e a {dados.faixa_maxima}.
          </p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              <Label className="text-[#A1A1AA]">Aluno</Label>
              <Select value={escolhido} onValueChange={setEscolhido}>
                <SelectTrigger data-testid="monitor-escolher-aluno" className="bg-[#0A0A0A] border-[#27272A]">
                  <SelectValue placeholder="Escolha o aluno" />
                </SelectTrigger>
                <SelectContent className="bg-[#141414] border-[#27272A]">
                  {elegiveis.map((c) => (
                    <SelectItem key={c.athlete_id} value={c.athlete_id} className="text-white">
                      {c.name} — {c.belt || 'sem faixa'}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-[#A1A1AA]">Motivo (opcional)</Label>
              <Input
                data-testid="monitor-motivo-promocao"
                value={motivoPromocao}
                onChange={(e) => setMotivoPromocao(e.target.value)}
                placeholder="Ex.: ajuda na turma da noite"
                className="bg-[#0A0A0A] border-[#27272A] text-white"
              />
            </div>
            <div className="sm:col-span-2">
              <Button
                data-testid="monitor-promover"
                onClick={promover}
                disabled={salvando}
                className="bg-[#D4AF37] text-black hover:bg-[#C4A030]"
              >
                {salvando ? <Loader2 className="animate-spin mr-1" size={16} /> : null}
                Promover a Monitor
              </Button>
            </div>
          </div>
        )}
        {candidatos.some((c) => !c.pode_promover && c.motivo) ? (
          <ul className="text-xs text-[#71717A] space-y-1" data-testid="monitores-recusados">
            {candidatos.filter((c) => !c.pode_promover && c.motivo).slice(0, 6).map((c) => (
              <li key={c.athlete_id}>
                {c.name}: {MOTIVOS_DE_RECUSA[c.motivo] || c.motivo}
              </li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="rounded-lg border border-[#27272A] bg-[#141414] p-4 space-y-3">
        <span className="font-medium text-white">Monitores da equipe ({monitores.length})</span>
        {monitores.length === 0 ? (
          <p className="text-sm text-[#A1A1AA]" data-testid="monitores-vazio">
            Nenhum Monitor nesta equipe ainda.
          </p>
        ) : (
          <ul className="divide-y divide-[#27272A]">
            {monitores.map((m) => (
              <li key={m.user_id} className="py-3" data-testid={`monitor-${m.user_id}`}>
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div>
                    <div className="text-white">
                      {m.name} <span className="text-[#71717A]">· {m.belt || 'sem faixa'}</span>
                    </div>
                    <div className="text-xs text-[#71717A]">
                      {m.team_name || 'equipe'} · promovido por {m.promoted_by_email || '—'}
                      {m.promoted_at ? ` em ${dataCurta(m.promoted_at)}` : ''}
                      {m.reason ? ` · motivo: ${m.reason}` : ''}
                    </div>
                  </div>
                  {m.pode_remover ? (
                    <Button
                      variant="outline"
                      size="sm"
                      data-testid={`monitor-remover-${m.user_id}`}
                      className="border-[#27272A] text-[#A1A1AA] hover:text-white"
                      onClick={() => { setRemovendo(m.user_id); setMotivoRemocao(''); }}
                    >
                      <Trash2 size={14} className="mr-1" /> Remover vínculo
                    </Button>
                  ) : (
                    <span className="text-xs text-[#71717A]">
                      {m.remocao_emergencial ? 'Remoção emergencial disponível' : 'Só quem promoveu remove'}
                    </span>
                  )}
                </div>
                {removendo === m.user_id ? (
                  <div className="mt-2 flex flex-wrap items-end gap-2">
                    <div className="min-w-[220px] flex-1">
                      <Label className="text-[#A1A1AA]">Motivo (opcional)</Label>
                      <Input
                        data-testid="monitor-motivo-remocao"
                        value={motivoRemocao}
                        onChange={(e) => setMotivoRemocao(e.target.value)}
                        className="bg-[#0A0A0A] border-[#27272A] text-white"
                      />
                    </div>
                    <Button
                      data-testid="monitor-confirmar-remocao"
                      onClick={() => remover(m.user_id)}
                      disabled={salvando}
                      className="bg-[#D4AF37] text-black hover:bg-[#C4A030]"
                    >
                      Confirmar remoção
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setRemovendo(null)}
                      className="border-[#27272A] text-[#A1A1AA]"
                    >
                      Cancelar
                    </Button>
                  </div>
                ) : null}
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
